import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Property, Tenant } from '../../types/properties';
import toast from 'react-hot-toast';

interface LeaseFormProps {
  propertyId: string;
  tenants: Tenant[];
  onClose: () => void;
  onSuccess: () => void;
}

export function LeaseForm({ propertyId, tenants, onClose, onSuccess }: LeaseFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    start_date: '',
    end_date: '',
    monthly_rent: '',
    security_deposit: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.tenant_id) {
      toast.error("Please select a tenant");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('leases').insert({
        property_id: propertyId,
        tenant_id: formData.tenant_id,
        start_date: formData.start_date,
        end_date: formData.end_date,
        monthly_rent: parseFloat(formData.monthly_rent),
        security_deposit: formData.security_deposit ? parseFloat(formData.security_deposit) : null,
        status: 'active'
      });

      if (error) throw error;

      // Update property status to occupied
      await supabase.from('properties').update({ status: 'occupied' }).eq('id', propertyId);

      toast.success('Lease created successfully');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Select Tenant</label>
          <select
            required
            value={formData.tenant_id}
            onChange={e => setFormData({ ...formData, tenant_id: e.target.value })}
            className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none"
          >
            <option value="">-- Select a Tenant --</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Start Date</label>
          <input required type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">End Date</label>
          <input required type="date" value={formData.end_date} onChange={e => setFormData({ ...formData, end_date: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Monthly Rent</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]">$</span>
            <input required type="number" step="0.01" value={formData.monthly_rent} onChange={e => setFormData({ ...formData, monthly_rent: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Security Deposit</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]">$</span>
            <input type="number" step="0.01" value={formData.security_deposit} onChange={e => setFormData({ ...formData, security_deposit: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-[var(--t-primary)] text-white text-sm font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50">
          {isSubmitting ? 'Saving...' : 'Create Lease'}
        </button>
      </div>
    </form>
  );
}
